import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, suporte, type Paginado } from '../api/client';
import { SEGMENTOS } from '../dados/segmentos';
import { CamposLocalidade } from '../components/CamposLocalidade';
import { ConfigurarCliente } from './ConfigurarCliente';
import { mensagemDeAcesso } from '../dados/mensagem-acesso';
import { Tag } from '../components/Tag';
import {
  Campo,
  CampoBusca,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  PageHeader,
  Paginacao,
  Vazio,
} from '../components/ui';
import { limpar } from '../pages/Eventos';

interface Organizacao {
  id: string;
  legalName: string;
  tradeName: string;
  country: string;
  state: string | null;
  city: string | null;
  segment: string | null;
  status: string;
  contactEmail: string | null;
  createdAt: string;
  _count?: { users: number; events: number };
}

interface RespostaCriacao {
  organization: Organizacao;
  admin: { name: string; email: string };
  /** Nulo quando a senha foi definida na criação. */
  inviteToken: string | null;
  inviteExpiresAt: string | null;
}

/**
 * A senha nunca volta do servidor — ele guarda só o hash. Ela vem do campo que
 * acabou de ser preenchido, vive nesta tela e some quando o aviso é fechado.
 */
type Criada = RespostaCriacao & { senhaDefinida?: string };

/** O mesmo texto da redefinição de senha, montado com o que a criação devolveu. */
function mensagemDaCriacao(criada: Criada) {
  return mensagemDeAcesso({
    cliente: criada.organization.tradeName,
    email: criada.admin.email,
    senha: criada.senhaDefinida,
    inviteToken: criada.inviteToken,
  });
}

interface RespostaSuporte {
  accessToken: string;
  organization: { id: string; tradeName: string; status: string };
  reason: string;
}

const CORES_STATUS: Record<string, string> = {
  ATIVA: 'success',
  INATIVA: 'muted',
  BLOQUEADA: 'danger',
  SUSPENSA: 'warning',
};

/**
 * Carteira de clientes da plataforma.
 *
 * Era `/organizacoes`, dentro do painel do cliente. Mudou de lugar em vez de
 * ganhar uma segunda versão no Master: é a MESMA tela, e duas cópias
 * envelheceriam em ritmos diferentes.
 */
export function Clientes() {
  const cliente = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  const [criada, setCriada] = useState<Criada | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [configurando, setConfigurando] = useState<string | null>(null);

  // Senha controlada porque a confirmação precisa comparar enquanto se digita —
  // descobrir a divergência só no envio é o pior momento para descobrir.
  const [senha, setSenha] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');

  const erroSenha =
    senhaConfirma && senha !== senhaConfirma
      ? 'As senhas não conferem.'
      : senha && senha.length < 10
        ? 'Use ao menos 10 caracteres.'
        : undefined;

  /** Zera o formulário ao fechar, para o próximo cadastro não herdar a senha. */
  function fecharCriacao() {
    setCriando(false);
    setSenha('');
    setSenhaConfirma('');
  }

  const params = new URLSearchParams({ page: String(pagina), perPage: '20' });
  if (busca) params.set('search', busca);

  const consulta = useQuery({
    queryKey: ['organizacoes', pagina, busca],
    queryFn: () => api<Paginado<Organizacao>>(`/organizations?${params}`),
  });

  const criar = useMutation({
    mutationFn: (dados: Record<string, string>) => {
      const {
        adminName,
        adminEmail,
        adminPassword,
        adminPasswordConfirm: _confirmacao,
        ...org
      } = dados;

      return api<RespostaCriacao>('/organizations', {
        method: 'POST',
        body: {
          ...limpar(org),
          admin: {
            name: adminName,
            email: adminEmail,
            // Só vai quando preenchida: campo vazio significa "manda convite",
            // e uma string vazia no corpo seria recusada pelo `min(10)`.
            ...(adminPassword ? { password: adminPassword } : {}),
          },
        },
      });
    },
    onSuccess: (resposta) => {
      cliente.invalidateQueries({ queryKey: ['organizacoes'] });
      // Lida ANTES de `fecharCriacao` zerar o formulário — depois dela a senha
      // não existe mais em lugar nenhum, nem aqui nem no servidor.
      setCriada({ ...resposta, senhaDefinida: senha || undefined });
      fecharCriacao();
    },
  });

  /** Fecha o aviso e descarta a senha da memória da tela. */
  function fecharAviso() {
    setCriada(null);
    setCopiado(false);
  }

  async function copiar(texto: string) {
    await navigator.clipboard.writeText(texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  /**
   * Acesso ao ambiente de um cliente. Um clique, sem motivo obrigatório — a
   * entrada continua auditada (quem, quando, qual cliente).
   *
   * ⚠️ NÃO é somente leitura, e este comentário e a dica do botão diziam que era.
   * O modo suporte lê e escreve, por decisão de produto (`auth.types.ts`), e o
   * banner e a API sempre disseram isso. A dica é a primeira coisa que quem
   * entra lê — dizer "somente leitura" ali convidava a mexer achando que nada
   * mudaria.
   */
  const entrarSuporte = useMutation({
    mutationFn: (id: string) =>
      api<RespostaSuporte>('/master/support', {
        method: 'POST',
        body: { organizationId: id },
      }),
    onSuccess: (resposta) => {
      suporte.entrar({
        token: resposta.accessToken,
        organizationId: resposta.organization.id,
        organizationName: resposta.organization.tradeName,
        reason: resposta.reason,
      });
      // Recarga completa, não navegação do router: o cache do React Query está
      // cheio de dados da PLATAFORMA e não pode atravessar para dentro do
      // cliente. Ver o comentário de `suporte` em api/client.ts.
      window.location.href = '/dashboard';
    },
  });

  return (
    <>
      <PageHeader
        titulo="Clientes"
        subtitulo="Organizações que contratam a plataforma."
        acoes={
          <button className="btn btn-primary" onClick={() => setCriando(true)}>
            <i className="ti ti-building-plus" /> Nova organização
          </button>
        }
      />

      <div className="barra-filtros">
        <CampoBusca
          valor={busca}
          aoMudar={(v) => {
            setBusca(v);
            setPagina(1);
          }}
          placeholder="Buscar por razão social, nome fantasia ou e-mail…"
        />
      </div>

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio icone="ti-building" titulo="Nenhuma organização encontrada" />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Organização</th>
                    <th>Local</th>
                    <th>Usuários</th>
                    <th>Eventos</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((org) => (
                    <tr key={org.id}>
                      <td>
                        <div className="celula-principal">{org.tradeName}</div>
                        <div className="celula-secundaria">{org.legalName}</div>
                      </td>
                      <td>
                        {[org.city, org.state, org.country].filter(Boolean).join(' / ')}
                        {org.segment && <div className="celula-secundaria">{org.segment}</div>}
                      </td>
                      <td>{org._count?.users ?? 0}</td>
                      <td>{org._count?.events ?? 0}</td>
                      <td>
                        <Tag type={CORES_STATUS[org.status] ?? 'muted'}>{org.status}</Tag>
                      </td>
                      <td>
                        <div className="acoes-linha">
                          <button
                            className="btn btn-secondary btn-sm"
                            title="Entrar no ambiente deste cliente — o que você alterar fica registrado em seu nome"
                            disabled={entrarSuporte.isPending}
                            onClick={() => entrarSuporte.mutate(org.id)}
                          >
                            <i className="ti ti-eye" /> Acessar
                          </button>

                          <button
                            className="btn btn-secondary btn-sm"
                            title="Dados cadastrais, equipe e exclusão"
                            onClick={() => setConfigurando(org.id)}
                          >
                            <i className="ti ti-settings" /> Configurar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
          </div>
        ))}

      {entrarSuporte.isError && (
        <div style={{ marginTop: 16 }}>
          <ErroBox erro={entrarSuporte.error} />
        </div>
      )}

      {configurando && (
        <ConfigurarCliente id={configurando} aoFechar={() => setConfigurando(null)} />
      )}

      {criando && (
        <Modal
          titulo="Nova organização"
          descricao="O primeiro administrador é criado junto — uma organização sem ninguém que possa entrar não serve para nada."
          aoFechar={fecharCriacao}
          largura={620}
        >
          <Form aoEnviar={(dados) => criar.mutate(dados)}>
            {criar.isError && <div className="form-erro">{(criar.error as Error).message}</div>}

            <LinhaCampos>
              <Campo label="Razão social" name="legalName" obrigatorio autoFocus />
              <Campo label="Nome fantasia" name="tradeName" obrigatorio />
            </LinhaCampos>

            <LinhaCampos>
              <Campo
                label="Segmento"
                name="segment"
                opcoes={[
                  { valor: '', rotulo: 'Selecione…' },
                  ...SEGMENTOS.map((s) => ({ valor: s, rotulo: s })),
                ]}
              />
              <div />
            </LinhaCampos>

            <CamposLocalidade />

            <div className="divider" style={{ margin: '4px 0 18px' }} />
            <div className="secao-titulo">
              <i className="ti ti-user-shield" /> Primeiro administrador
            </div>

            <LinhaCampos>
              <Campo label="Nome" name="adminName" obrigatorio />
              <Campo label="E-mail" name="adminEmail" tipo="email" obrigatorio />
            </LinhaCampos>

            <LinhaCampos>
              <Campo
                label="Senha"
                name="adminPassword"
                tipo="password"
                valor={senha}
                aoMudar={setSenha}
                ajuda="Ao menos 10 caracteres. Deixe em branco para enviar convite."
              />
              <Campo
                label="Confirmar senha"
                name="adminPasswordConfirm"
                tipo="password"
                valor={senhaConfirma}
                aoMudar={setSenhaConfirma}
                erro={erroSenha}
              />
            </LinhaCampos>

            {senha && (
              <div className="aviso" style={{ marginBottom: 16 }}>
                <i className="ti ti-info-circle" />
                <span>
                  A conta nasce ativa e <strong>você</strong> conhece a senha. Peça a troca no
                  primeiro acesso — ou deixe os dois campos em branco para que a pessoa defina
                  a própria senha pelo link de convite.
                </span>
              </div>
            )}

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={fecharCriacao}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={criar.isPending || !!erroSenha}
              >
                {criar.isPending ? 'Criando…' : 'Criar organização'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {criada && (
        <Modal
          titulo="Organização criada"
          descricao={
            criada.inviteToken
              ? `Envie o link abaixo para ${criada.admin.name} definir a senha.`
              : `${criada.admin.name} já pode entrar com a senha que você definiu.`
          }
          aoFechar={fecharAviso}
          largura={560}
        >
          <div className="aviso" style={{ marginBottom: 16 }}>
            <i className="ti ti-alert-triangle" />
            <span>
              {criada.senhaDefinida
                ? 'Copie agora: ao fechar esta janela a senha some. O servidor guarda só o hash.'
                : 'Copie agora: o link aparece uma única vez — o servidor guarda só o hash.'}
            </span>
          </div>

          <label className="campo">
            <span className="campo-label">Mensagem para {criada.admin.name}</span>
            <textarea
              className="form-input mono"
              readOnly
              rows={criada.senhaDefinida ? 8 : 7}
              value={mensagemDaCriacao(criada)}
              style={{ resize: 'vertical', lineHeight: 1.6 }}
              onFocus={(e) => e.target.select()}
            />
          </label>

          <ModalRodape>
            <button
              className="btn btn-secondary"
              onClick={() => void copiar(mensagemDaCriacao(criada))}
            >
              <i className={`ti ${copiado ? 'ti-check' : 'ti-copy'}`} />
              {copiado ? 'Copiado' : 'Copiar mensagem'}
            </button>
            <button className="btn btn-primary" onClick={fecharAviso}>
              Fechar
            </button>
          </ModalRodape>
        </Modal>
      )}
    </>
  );
}
